"use client"

import { useEffect, useState, useMemo } from "react"
import { Box, CircularProgress, createTheme, ThemeProvider, Typography } from "@mui/material"
import Widget from "./components/Widget"
import { getWhatsappWidgetInfo } from "./services/restAPIServices"

// Define defaultWidgetInfo outside the component so it's a stable reference
const defaultWidgetInfo = {
  agentsEnabled: false,
  agents: [],
  widgetConfig: {
    color: "#FFFFFF",
    background: "#085E56",
    heading: "Let's connect on Whatsapp",
    greeting: "How Can I Help You Today?",
    logo: "default",
    location: "right",
    redirectNumber: "",
  },
}

function App() {
  const theme = createTheme({
    typography: { fontFamily: "DM Sans, sans-serif" },
    breakpoints: {
      values: { xs: 0, sm: 400, md: 900, lg: 1200, xl: 1425 },
    },
  })

  // useMemo ensures these values are only calculated once
  const { widgetId, bgColor } = useMemo(() => {
    const params = new URLSearchParams(window.location.search)
    return {
      widgetId: params.get("widgetId"),
      bgColor: params.get("bgc"),
    }
  }, [])

  const [loading, setLoading] = useState(true)
  const [widgetInfo, setWidgetInfo] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    // Define the async function inside the effect
    const fetchWidgetData = async () => {
      if (!widgetId) {
        setWidgetInfo(defaultWidgetInfo)
        setLoading(false)
        return
      }

      try {
        const response = await getWhatsappWidgetInfo(widgetId)
        if (response.status === 200) {
          setWidgetInfo(response.data)
        } else {
          throw new Error(`API responded with status: ${response.status}`)
        }
      } catch (error) {
        console.error("Error getting Bot Info", error)
        setError("Failed to load widget. Please try again later.")
        // Use the stable defaultWidgetInfo from the outer scope
        setWidgetInfo(defaultWidgetInfo)
      } finally {
        // This ensures loading is set to false regardless of success or failure
        setLoading(false)
      }
    }

    fetchWidgetData()
  }, [widgetId]) // Only re-run if widgetId changes

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ height: "100vh", background: bgColor ?? "#FFFFFF", borderRadius: "15px" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <CircularProgress variant="indeterminate" size={50} sx={{ color: bgColor ?? "#085E56" }} />
          </Box>
        ) : (
          <>
            {error && (
              <Box sx={{ p: 2, m: 2, backgroundColor: "error.light", color: "error.contrastText", borderRadius: 1 }}>
                <Typography>{error}</Typography>
              </Box>
            )}
            <Widget widgetInfo={widgetInfo ?? defaultWidgetInfo} />
          </>
        )}
      </Box>
    </ThemeProvider>
  )
}

export default App
